import React,
{
  useEffect,
  useState,
} from "react";

import { Layout, Spin } from "antd";

import PageBreadcrumb from "../../Common/UIComponents/PageBreadcrumb";
import SideNav, { getSideNavCollapsed } from "../../Common/UIComponents/SideNav";
import PaymentPendingBanner from "../../grace-period/components/PaymentPendingBanner";
import PickupTab from "./PickupRequests/PickupTab";
import ConfirmationStep from "./PickupRequests/Steps/ConfirmationStep";
import PackagingBoxesStep from "./PickupRequests/Steps/PackagingBoxesStep";
// import PaymentStep from "./PickupRequests/Steps/PaymentStep";
import PickupScheduleStep from "./PickupRequests/Steps/PickupScheduleStep";
import SelectSampleSubmissions from "./PickupRequests/Steps/SelectSampleSubmissions";
import SummaryStep from "./PickupRequests/Steps/SummaryStep";
import CreateEditSupply from "./Supply/CreateEditSupply";
import SupplyTab from "./Supply/SupplyTab";

import { hasPermission, permissions } from "../../../utils/Permissions";
import { isLocalCourierType } from "./PickupRequests/utils";

import {
  fetchPickupRequests,
  fetchRecurringRequests,
  fetchSupplyRequests,
} from "../../../actions/supplyPickup";

import { ReactComponent as BoxIcon } from "../../../assets/images/supply-pickup/Box.svg";
import { ReactComponent as ShoppingCartIcon } from "../../../assets/images/supply-pickup/ShoppingCart.svg";

import "./SupplyPickupTabGroup.css";

const { Sider, Content } = Layout;

const TabLinkInfo = {
  supply: {
    key: "1",
    path: "/supply-pickup/supply",
  },
  pickup: {
    key: "2",
    path: "/supply-pickup/pickup",
  },
};

const getDefaultTab = (hasSupplyAccess, hasPickupAccess) => {
  if (hasSupplyAccess) return TabLinkInfo.supply;
  if (hasPickupAccess) return TabLinkInfo.pickup;
  return null;
};

const getCurrentTab = (hasSupplyAccess, hasPickupAccess) => {
  const url = window.location.href;
  if (url.includes(TabLinkInfo.pickup.path) && hasPickupAccess) {
    return TabLinkInfo.pickup;
  }
  if (hasSupplyAccess) {
    return TabLinkInfo.supply;
  }
  return getDefaultTab(hasSupplyAccess, hasPickupAccess);
};

const defaultStatus = "exclude-cancelled";

const fetchInitialSupplyData = () => Promise.all([
  fetchSupplyRequests({
    supply_order_id: "",
    status: defaultStatus,
    date_from: "",
    date_to: "",
    page: 1,
  }),
  fetchRecurringRequests({ page: 1 }),
]);

const fetchInitialPickupData = () => fetchPickupRequests({
  pickup_order_id: "",
  status: defaultStatus,
  pickup_category: "",
  date_from: "",
  date_to: "",
  page: 1,
});

const getSupplyHasData = (supplyResp, recurringResp) => (
  (supplyResp?.success && (supplyResp.result || []).length > 0)
  || (recurringResp?.success && ((recurringResp.count ?? 0) > 0 || (recurringResp.result || []).length > 0))
);

const getPickupHasData = (pickupResp) => (
  pickupResp?.success && (pickupResp.result || []).length > 0
);

export default function SupplyPickupTabGroup({ history }) {
  const hasSupplyAccess = hasPermission(permissions.schedules.supplyRequest);
  const hasPickupAccess = hasPermission(permissions.schedules.pickupRequest);
  const [activeKey, setActiveKey] = useState(
    () => getCurrentTab(hasSupplyAccess, hasPickupAccess)?.key ?? null,
  );
  const [sideNavCollapsed, setSideNavCollapsed] = useState(getSideNavCollapsed());
  const [createEditSupply, setCreateEditSupply] = useState(null);
  const [createEditPickup, setCreateEditPickup] = useState(null);
  const [initialCheckDone, setInitialCheckDone] = useState(!hasSupplyAccess && !hasPickupAccess);
  const [showSupplyLanding, setShowSupplyLanding] = useState(false);
  const [showPickupLanding, setShowPickupLanding] = useState(false);
  const [initialSupplyResponse, setInitialSupplyResponse] = useState(null);
  const [initialRecurringResponse, setInitialRecurringResponse] = useState(null);
  const [initialPickupResponse, setInitialPickupResponse] = useState(null);

  const applySupplyFetchResults = (supplyResp, recurringResp) => {
    setInitialSupplyResponse(supplyResp);
    setInitialRecurringResponse(recurringResp);

    const supplyHasData = getSupplyHasData(supplyResp, recurringResp);
    const allFetchedSuccessfully = supplyResp?.success && recurringResp?.success;

    if (allFetchedSuccessfully && !supplyHasData) {
      setShowSupplyLanding(true);
    } else if (supplyHasData) {
      setShowSupplyLanding(false);
    }
  };

  const applyPickupFetchResults = (pickupResp) => {
    setInitialPickupResponse(pickupResp);

    if (pickupResp?.success && !getPickupHasData(pickupResp)) {
      setShowPickupLanding(true);
    } else if (getPickupHasData(pickupResp)) {
      setShowPickupLanding(false);
    }
  };

  const refreshSupplyLandingState = async () => {
    const [supplyResp, recurringResp] = await fetchInitialSupplyData();
    applySupplyFetchResults(supplyResp, recurringResp);
  };

  const refreshPickupLandingState = async () => {
    const pickupResp = await fetchInitialPickupData();
    applyPickupFetchResults(pickupResp);
  };

  useEffect(() => {
    let canceled = false;

    const checkForEmptyState = async () => {
      const [supplyResults, pickupResp] = await Promise.all([
        hasSupplyAccess ? fetchInitialSupplyData() : Promise.resolve(null),
        hasPickupAccess ? fetchInitialPickupData() : Promise.resolve(null),
      ]);

      if (canceled) return;

      if (hasSupplyAccess && supplyResults) {
        const [supplyResp, recurringResp] = supplyResults;
        applySupplyFetchResults(supplyResp, recurringResp);
      }
      if (hasPickupAccess && pickupResp) {
        applyPickupFetchResults(pickupResp);
      }
      setInitialCheckDone(true);
    };

    if (hasSupplyAccess || hasPickupAccess) {
      checkForEmptyState();
    }

    return () => {
      canceled = true;
    };
  }, [hasSupplyAccess, hasPickupAccess]);

  const tabItems = [
    hasSupplyAccess && {
      key: TabLinkInfo.supply.key,
      label: "Supply Schedules",
      icon: <ShoppingCartIcon />,
      content: (
        <SupplyTab
          onCreateEditClick={setCreateEditSupply}
          initialSupplyResponse={initialSupplyResponse}
          initialRecurringResponse={initialRecurringResponse}
          showLanding={showSupplyLanding}
          onSupplyDataMutated={refreshSupplyLandingState}
        />
      ),
    },
    hasPickupAccess && {
      key: TabLinkInfo.pickup.key,
      label: "Pickup Requests",
      icon: <BoxIcon />,
      content: (
        <PickupTab
          onCreateEditClick={setCreateEditPickup}
          initialPickupResponse={initialPickupResponse}
          showLanding={showPickupLanding}
        />
      ),
    },
  ].filter(Boolean);

  useEffect(() => {
    if (!initialCheckDone) return;

    const currentTab = getCurrentTab(hasSupplyAccess, hasPickupAccess);
    if (currentTab) {
      setActiveKey(currentTab.key);
      if (history.location.pathname !== currentTab.path) {
        history.replace({
          pathname: currentTab.path,
          state: history.location.state,
        });
      }
    }
  }, [history, hasSupplyAccess, hasPickupAccess, initialCheckDone]);

  const createPickupWizard = history.location?.state?.createPickupWizard;

  useEffect(() => {
    if (!hasPickupAccess) return;
    if (!createPickupWizard?.phase) return;

    setActiveKey(TabLinkInfo.pickup.key);
    setCreateEditPickup(createPickupWizard);
  }, [createPickupWizard, hasPickupAccess]);

  const handleSelect = (key) => {
    if (key === TabLinkInfo.supply.key && hasSupplyAccess) {
      setInitialSupplyResponse(null);
      setInitialRecurringResponse(null);
      setActiveKey(key);
      history.push(TabLinkInfo.supply.path);
      refreshSupplyLandingState();
    } else if (key === TabLinkInfo.pickup.key && hasPickupAccess) {
      setInitialPickupResponse(null);
      setActiveKey(key);
      history.push(TabLinkInfo.pickup.path);
      refreshPickupLandingState();
    } else {
      const defaultTab = getDefaultTab(hasSupplyAccess, hasPickupAccess);
      if (defaultTab) {
        setActiveKey(defaultTab.key);
        history.push(defaultTab.path);
      }
    }
  };

  const clearCreatePickupWizardLocationState = () => {
    if (!history.location?.state?.createPickupWizard) return;
    history.replace({
      pathname: history.location.pathname || TabLinkInfo.pickup.path,
    });
  };

  const returnToMainPage = async (tabIndex) => {
    if (tabIndex === TabLinkInfo.supply.key && hasSupplyAccess) {
      await refreshSupplyLandingState();
    }
    if (tabIndex === TabLinkInfo.pickup.key && hasPickupAccess) {
      await refreshPickupLandingState();
    }
    setActiveKey(tabIndex);
    setCreateEditSupply(null);
    setCreateEditPickup(null);
    clearCreatePickupWizardLocationState();
  };

  const handleSupplyMutationSuccess = () => returnToMainPage("1");

  if (createEditSupply) {
    return (
      <CreateEditSupply
        requestData={createEditSupply}
        returnToMainPage={returnToMainPage}
        onCreateSuccess={handleSupplyMutationSuccess}
      />
    );
  }

  if (createEditPickup?.phase === "select-submissions") {
    return (
      <SelectSampleSubmissions
        pickupTypeLabel={createEditPickup.pickupTypeLabel || "FedEx Pickup"}
        pickupTypeKey={createEditPickup.pickupTypeKey || "fedex-pickup"}
        initialSelectedSubmissions={createEditPickup.selectedSubmissions || []}
        onBack={() => returnToMainPage("2")}
        onContinue={(selectedSubmissions) => {
          setCreateEditPickup((prev) => ({
            ...prev,
            phase: "pickup-schedule",
            selectedSubmissions,
          }));
        }}
      />
    );
  }

  if (createEditPickup?.phase === "pickup-schedule") {
    const skipPackaging = isLocalCourierType(createEditPickup.pickupTypeKey);
    return (
      <PickupScheduleStep
        pickupTypeLabel={createEditPickup.pickupTypeLabel || "FedEx Pickup"}
        pickupTypeKey={createEditPickup.pickupTypeKey || "fedex-pickup"}
        initialSchedule={createEditPickup.schedule || null}
        onBack={() => {
          setCreateEditPickup({
            ...createEditPickup,
            phase: "select-submissions",
          });
        }}
        onContinue={(schedule) => {
          setCreateEditPickup((prev) => ({
            ...prev,
            phase: skipPackaging ? "summary" : "packaging-boxes",
            schedule,
            ...(skipPackaging ? {
              packaging: null,
              shippingMethod: null,
              trackingIds: [],
            } : {}),
          }));
        }}
      />
    );
  }

  if (createEditPickup?.phase === "packaging-boxes") {
    return (
      <PackagingBoxesStep
        pickupTypeLabel={createEditPickup.pickupTypeLabel || "FedEx Pickup"}
        pickupTypeKey={createEditPickup.pickupTypeKey || "fedex-pickup"}
        selectedSubmissions={createEditPickup.selectedSubmissions || []}
        initialPackaging={createEditPickup.packaging || null}
        initialShippingMethod={createEditPickup.shippingMethod || null}
        initialTrackingIds={createEditPickup.trackingIds || []}
        onBack={() => {
          setCreateEditPickup({
            ...createEditPickup,
            phase: "pickup-schedule",
          });
        }}
        onContinue={({ packaging, shippingMethod, trackingIds }) => {
          setCreateEditPickup((prev) => ({
            ...prev,
            // Skip payment for now — was "payment"; restore when re-enabling PaymentStep
            phase: "summary",
            packaging,
            shippingMethod,
            trackingIds,
            // payment: undefined, // set via PaymentStep onConfirm when re-enabled
          }));
        }}
      />
    );
  }

  // Hidden for now — re-enable when payment step is ready
  // if (createEditPickup?.phase === "payment") {
  //   return (
  //     <PaymentStep
  //       pickupTypeLabel={createEditPickup.pickupTypeLabel || "FedEx Pickup"}
  //       schedule={createEditPickup.schedule || null}
  //       packaging={createEditPickup.packaging || null}
  //       onBack={() => {
  //         setCreateEditPickup({
  //           ...createEditPickup,
  //           phase: "packaging-boxes",
  //         });
  //       }}
  //       onConfirm={(payment) => {
  //         setCreateEditPickup({
  //           ...createEditPickup,
  //           phase: "summary",
  //           payment,
  //         });
  //       }}
  //     />
  //   );
  // }

  if (createEditPickup?.phase === "summary") {
    const skipPackaging = isLocalCourierType(createEditPickup.pickupTypeKey);
    return (
      <SummaryStep
        pickupTypeLabel={createEditPickup.pickupTypeLabel || "FedEx Pickup"}
        pickupTypeKey={createEditPickup.pickupTypeKey || "fedex-pickup"}
        schedule={createEditPickup.schedule || null}
        packaging={createEditPickup.packaging || null}
        shippingMethod={createEditPickup.shippingMethod || null}
        trackingIds={createEditPickup.trackingIds || []}
        selectedSubmissions={createEditPickup.selectedSubmissions || []}
        onBack={() => {
          setCreateEditPickup({
            ...createEditPickup,
            phase: skipPackaging ? "pickup-schedule" : "packaging-boxes",
          });
        }}
        onSubmitSuccess={(confirmation) => {
          setCreateEditPickup({
            ...createEditPickup,
            phase: "confirmation",
            confirmation: confirmation || null,
          });
        }}
      />
    );
  }

  if (createEditPickup?.phase === "confirmation") {
    return (
      <ConfirmationStep
        pickupTypeLabel={createEditPickup.pickupTypeLabel || "FedEx Pickup"}
        pickupTypeKey={createEditPickup.pickupTypeKey || "fedex-pickup"}
        schedule={createEditPickup.schedule || null}
        packaging={createEditPickup.packaging || null}
        confirmation={createEditPickup.confirmation || null}
        onBack={() => returnToMainPage("2")}
        onReturnToDashboard={() => returnToMainPage("2")}
      />
    );
  }

  const sideNavItems = tabItems.map(({ key, label, icon }) => ({
    key,
    label,
    icon,
  }));

  const activeItem = tabItems.find((item) => item.key === activeKey) || tabItems[0];

  const renderSchedulesLayout = (content) => {
    if (!activeKey || sideNavItems.length === 0) return null;

    return (
      <div className="SupplyPickupTabGroup__Container">
        {activeItem && (
          <PageBreadcrumb
            items={[
              { label: "Schedules" },
              { label: activeItem.label },
            ]}
            showBack={false}
          />
        )}
        <Layout className="SchedulesLayout">
          <Sider
            width={220}
            collapsedWidth={64}
            collapsed={sideNavCollapsed}
            theme="light"
            trigger={null}
            className="SchedulesLayout__Sider"
          >
            <SideNav
              ariaLabel="Schedules navigation"
              items={sideNavItems}
              activeKey={activeKey}
              collapsed={sideNavCollapsed}
              onCollapsedChange={setSideNavCollapsed}
              onItemClick={(item) => handleSelect(item.key)}
            />
          </Sider>
          <Content className="SchedulesLayout__Content">
            <PaymentPendingBanner />
            {content}
          </Content>
        </Layout>
      </div>
    );
  };

  if (!initialCheckDone) {
    return renderSchedulesLayout(
      <div className="SchedulesLayout__Loading">
        <Spin size="large" />
      </div>,
    );
  }

  if (!activeKey || tabItems.length === 0) return null;

  return renderSchedulesLayout(activeItem ? activeItem.content : null);
}
